/** Realtor.com session card (Tom 2026-08-14: "do realtor.com — need to sign in
 * on the server to over come the sign in issue"). Realtor's permit history is
 * the same public-records data the county holds, but the full list hides behind
 * a "Show more" and shows complete only to a signed-in session. The worker
 * renders the property page in a real browser over a residential IP carrying
 * THIS cookie, expands "Show more", and reads all the permits — used only as
 * tier 3, after county + city portals come up empty.
 *
 * Tom pastes his realtor.com cookie once (his Google password NEVER touches the
 * server — realtor's own session cookie is the login). We keep only the auth
 * cookies (REMEMBER_ME / MyAccountRUI / lithium / rdc_*), never the ad jar, and
 * never display cookie values back. */
import { useCallback, useEffect, useState } from "react";
import { backendFetch } from "../../lib/backend";

interface Status {
  configured: boolean;
  cookie_names?: string[];
  updated_at?: string | null;
  last_health?: { at?: string; ok?: boolean; count?: number; reason?: string; signed_in?: boolean } | null;
}

interface TestResult { ok: boolean; count: number; reason?: string; signed_in?: boolean }

function ago(iso?: string | null): string {
  if (!iso) return "never";
  const t = Date.parse(iso.endsWith("Z") ? iso : iso + "Z");
  if (!Number.isFinite(t)) return "unknown";
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.round(m / 60)}h ago`;
}

export function RealtorSession({ token, isAdmin }: { token: string | null; isAdmin: boolean }) {
  const [st, setSt] = useState<Status | null>(null);
  const [cookie, setCookie] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setSt(await backendFetch<Status>(token, "/api/scrapers/realtor-session/status", { timeoutMs: 20000 })); }
    catch { /* transient */ }
  }, [token]);
  useEffect(() => { if (isAdmin) void load(); }, [isAdmin, load]);

  if (!isAdmin) return null;

  const save = async () => {
    if (busy) return;
    setBusy(true); setNote(null);
    try {
      const r = await backendFetch<{ ok: boolean; cookie_names: string[] }>(token, "/api/scrapers/realtor-session", {
        method: "POST", body: { cookie }, timeoutMs: 20000,
      });
      setCookie("");
      setNote(r.cookie_names?.length ? `Saved ✓ — kept ${r.cookie_names.length} session cookie${r.cookie_names.length === 1 ? "" : "s"}.` : "Cleared.");
      await load();
    } catch (e) {
      setNote("Save failed: " + (e instanceof Error ? e.message : "error"));
    } finally { setBusy(false); }
  };

  const test = async () => {
    if (busy) return;
    setBusy(true); setNote("Rendering a known 9-permit property through the browser… (up to ~60s)");
    try {
      const r = await backendFetch<TestResult>(token, "/api/scrapers/realtor-session/test", { method: "POST", timeoutMs: 90000 });
      setNote(r.ok
        ? `✓ Working — pulled ${r.count} permits from realtor.com${r.signed_in === false ? " (but session looks signed out — re-paste for the full list)" : ""}.`
        : `✗ ${r.reason || "did not return the full permit set"}${r.signed_in === false ? " — session signed out; paste a fresh cookie." : ""}`);
      await load();
    } catch (e) {
      setNote("Test failed: " + (e instanceof Error ? e.message : "error"));
    } finally { setBusy(false); }
  };

  const h = st?.last_health;

  return (
    <div className="zfpurge">
      <div className="zfphd">
        <div>
          <b>Realtor.com session <span className="sub">· tier-3 permit fallback</span></b>
          <div className="sub">
            Used only when county + city portals return nothing. Paste your realtor.com cookie so the
            server reads permits as your signed-in session. Your Google password is never stored.
          </div>
        </div>
        <div className="zfpacts">
          {st === null ? <span className="sub dim">Loading…</span>
            : st.configured
              ? <span className={"cmppill " + (h?.ok === false ? "dead" : "done")}>{h?.ok === false ? "CHECK" : "CONFIGURED"}</span>
              : <span className="cmppill">NOT SET</span>}
        </div>
      </div>

      {st?.configured ? (
        <div className="sub" style={{ marginTop: 6 }}>
          {st.cookie_names?.length ? <>Cookies on file: <code>{st.cookie_names.join(", ")}</code>. </> : null}
          Updated {ago(st.updated_at)}.
          {h ? <> Last test {ago(h.at)}: {h.ok ? `${h.count} permits ✓` : `failed — ${h.reason || "—"}`}.</> : null}
        </div>
      ) : null}

      <textarea
        className="rs-cookie"
        placeholder={"Paste your realtor.com cookie here.\nChrome → open realtor.com (signed in) → DevTools (⌥⌘I) → Console → type:  document.cookie  → copy the whole line → paste here."}
        value={cookie}
        onChange={(e) => setCookie(e.target.value)}
        rows={3}
        style={{ width: "100%", marginTop: 10, fontFamily: "monospace", fontSize: 12 }}
      />
      <div className="mailacts" style={{ marginTop: 8 }}>
        <button className="btn slim primary" disabled={busy || !cookie.trim()} onClick={() => void save()}>Save cookie</button>
        <button className="btn slim" disabled={busy || !st?.configured} onClick={() => void test()}>Test now</button>
        {st?.configured ? <button className="btn slim" disabled={busy} onClick={() => { setCookie(""); void backendFetch(token, "/api/scrapers/realtor-session", { method: "POST", body: { cookie: "" }, timeoutMs: 20000 }).then(load); }}>Clear</button> : null}
      </div>
      {note ? <div className="cmpnote" style={{ marginTop: 8 }}>{note}</div> : null}
    </div>
  );
}
